import React, { Component } from 'react';
// redux
import { connect } from 'react-redux';
import { compose } from 'redux';
import { addSkill, editSkill } from '../../../redux/reducers/skills';
// helpers
import {
  skillMapPropsToValues,
  handleSkillSubmit,
  skillValidationSchema
} from '../helpers';
// utils
import moment from 'moment';
// hoc
import { withFormik } from 'formik';
// components
import { Collapse } from 'react-collapse';
import Select from '../../../components/Select/Select';
import Input from '../../../components/Input/Input';
import Button from '../../../components/Button/Button';
import FieldError from '../../../components/Form/FieldError';
import Form from '../../../components/Form/Form';
import CheckBox from '../../../components/CheckBox/CheckBox';
import Datepicker from '../../../components/Datepicker/Datepicker';
import RadioGroup from '../../../components/RadioGroup/RadioGroup';
// styles
import styles from './UpdateSkill.mod.css';
import classnames from 'classnames/bind';

const cx = classnames.bind(styles);

class UpdateSkill extends Component {
  constructor(props) {
    super(props);
    this.skillsOptions = this.getDefaultOptions();
  }

  skillPeriodOptions = [
    { value: 'day', label: 'Day(s)' },
    { value: 'week', label: 'Week(s)' },
    { value: 'month', label: 'Month(s)' },
    { value: 'year', label: 'Year(s)' }
  ];

  endsRadioOptions = [
    {
      value: 'never',
      label: 'Never'
    },
    {
      value: 'on',
      label: 'On'
    }
  ];

  handleSelectChange = (name, select) => {
    const { setFieldValue } = this.props;
    select && setFieldValue(name, select.value);
  };

  handleSkillChange = select => this.handleSelectChange('skillValue', select);
  handlePeriodChange = select => this.handleSelectChange('period', select);

  dateOnChange = (value, name) => {
    const { setFieldValue, setFieldTouched } = this.props;
    setFieldTouched(name, true);
    setFieldValue(name, new Date(value.utc().format()));
  };

  startDateOnChange = value => this.dateOnChange(value, 'startDate');
  endDateOnChange = value => this.dateOnChange(value, 'endDate');

  getDefaultOptions = () => {
    const { defaultSkills, values } = this.props;

    const options = defaultSkills.map(skill => ({
      value: skill,
      label: skill
    }));

    values.skillValue &&
      options.push({
        value: values.skillValue,
        label: values.skillValue
      });

    return options;
  };

  render() {
    const {
      values,
      touched,
      errors = {},
      isSubmitting,
      handleBlur,
      handleSubmit,
      handleChange,
      closeModal,
      modalData: { color, skill }
    } = this.props;

    const title = skill ? 'Edit Skill' : 'Adding new skill';
    const submitTitle = skill ? 'Save' : 'Add Skill';

    return (
      <Form className={cx('wrapper')} onSubmit={handleSubmit}>
        <h2 className={cx('title')}>{title}</h2>
        <div className={cx('form-control')}>
          <label className={cx('label')} htmlFor="skillValue">
            Personal skill
          </label>
          <div>
            <Select
              isCreatable
              placeholder="Type or select from scope"
              name="skillValue"
              value={values.skillValue}
              defaultInputValue={values.skillValue}
              options={this.skillsOptions}
              onChange={this.handleSkillChange}
            />
            <FieldError
              error={errors.skillValue}
              touched={touched.skillValue}
            />
          </div>
        </div>

        <div className={cx('form-control')}>
          <label className={cx('label')} htmlFor="isSkillRecurrence">
            Skill recurrence
          </label>
          <CheckBox
            name="isSkillRecurrence"
            checked={values.isSkillRecurrence}
            onChange={handleChange}
            onBlur={handleBlur}
            color={color}
            label="Add to calendar"
          />

          <Collapse isOpened={values.isSkillRecurrence}>
            <div className={cx('start-container')}>
              <label className={cx('start-label')}>Starts</label>
              <div className={cx('start-inputs')}>
                <Datepicker
                  color={color}
                  className={cx('date-picker')}
                  name="startDate"
                  selected={moment(values.startDate)}
                  onChange={this.startDateOnChange}
                  onBlur={this.startDateOnBlur}
                  invalid={touched.startDate && errors.startDate}
                />
              </div>
              <FieldError error={errors.startDate} touched />
            </div>

            <div className={cx('period-container')}>
              <label className={cx('period-label')}>Repeat every</label>
              <div className={cx('period-inputs')}>
                <Input
                  wrapperClassName={cx('period-days')}
                  color={color}
                  name="multiplicator"
                  type="number"
                  value={values.multiplicator}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  invalid={errors.multiplicator && touched.multiplicator}
                />
                <Select
                  className={cx('period-select')}
                  name="period"
                  value={values.period}
                  options={this.skillPeriodOptions}
                  onChange={this.handlePeriodChange}
                />
              </div>
              <FieldError
                error={errors.multiplicator}
                touched={touched.multiplicator}
              />
            </div>

            <div className={cx('end-container')}>
              <label className={cx('end-label')}>Ends</label>
              <div className={cx('end-inputs')}>
                <RadioGroup
                  vertical
                  name="isSkillHasEnd"
                  value={values.isSkillHasEnd}
                  onChange={handleChange}
                  color={color}
                  options={this.endsRadioOptions}
                />
                <Datepicker
                  disabled={values.isSkillHasEnd === 'never'}
                  color={color}
                  name="endDate"
                  className={cx('date-picker')}
                  selected={moment(values.endDate)}
                  onChange={this.endDateOnChange}
                  onBlur={this.endDateOnBlur}
                  invalid={touched.endDate && errors.endDate}
                />
              </div>
              <FieldError error={touched.endDate && errors.endDate} touched />
            </div>
          </Collapse>
        </div>

        <div className={cx('button-section')}>
          <Button plain onClick={closeModal} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            color={color}
            isLoading={isSubmitting}
            disabled={
              isSubmitting || (!values.defaultSkill && !values.skillValue)
            }
          >
            {submitTitle}
          </Button>
        </div>
      </Form>
    );
  }
}

const enhance = compose(
  connect(
    state => ({
      defaultSkills: state.skills.defaultSkills,
      userSkills: state.skills.userSkills
    }),
    { addSkill, editSkill }
  ),
  withFormik({
    mapPropsToValues: skillMapPropsToValues,
    validationSchema: skillValidationSchema,
    handleSubmit: handleSkillSubmit,
    displayName: 'AddNewSkillForm'
  })
);

export default enhance(UpdateSkill);



// WEBPACK FOOTER //
// ./src/containers/Skills/components/UpdateSkill.jsx